import React from 'react';
import { Download } from 'lucide-react';
import { CustomContextMenuItem, SpreadsheetData } from '../types';

interface SpreadsheetContextMenuProps {
  isVisible: boolean;
  position: { x: number; y: number };
  selectedCellsCount: number;
  onExportSelection: () => void;
  onClose: () => void;
  customMenuItems?: CustomContextMenuItem[];
  rightClickedRowData?: SpreadsheetData | null;
  rightClickedRowIndex?: number;
  rightClickedColumnKey?: string;
}

export function SpreadsheetContextMenu({
  isVisible,
  position,
  selectedCellsCount,
  onExportSelection,
  onClose,
  customMenuItems = [],
  rightClickedRowData = null,
  rightClickedRowIndex = -1,
  rightClickedColumnKey
}: SpreadsheetContextMenuProps) {
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-md shadow-lg min-w-[180px] py-1"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {/* Custom menu items */}
      {customMenuItems.length > 0 && customMenuItems.map((item) => (
        <button
          key={item.id}
          className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            item.onClick(rightClickedRowData, rightClickedRowIndex, rightClickedColumnKey);
            onClose();
          }}
          disabled={item.disabled}
        >
          {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
          {item.label}
        </button>
      ))}
      
      {/* Separator if there are custom items */}
      {customMenuItems.length > 0 && (
        <div className="border-t border-gray-200 my-1" />
      )}
      
      {/* Default CSV export option */}
      <button
        className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => {
          onExportSelection();
          onClose();
        }}
        disabled={selectedCellsCount === 0}
      >
        <Download size={16} />
        Export selection as CSV
      </button>
    </div>
  );
} 